<template>
  <v-app>
    <div v-if="showBootstrapOverlay" class="app-bootstrap-fill d-flex align-center justify-center">
      <v-progress-circular indeterminate color="orange darken-3" size="56" width="4" />
    </div>
    <component
      v-else
      :is="currentTemplate"
      :user="user"
      @save="saveUser()"
      @logout="logout()"
    />
  </v-app>
</template>
<script>
import { getAuth, sendEmailVerification, getAdditionalUserInfo, signOut } from 'firebase/auth'
import { getFirestore, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'
import { environment as firebaseClientEnvironment } from '@/firebase/init'
import { subscribeAuthBootstrap, whenAuthBootstrapReady } from '@/services/authBootstrap'
import { markSignedOut } from '@/services/signOutMarker'
import { isPrerenderBuild } from '@/utils/prerender'

// Templates are loaded as async components so each scope (public, admin,
// portal, vendor, ...) is code-split into its own chunk. Only the template
// matching the active route is downloaded — e.g. the public site never pulls
// in the admin chrome, and Springbok never pulls in the public footer.
export default {
  name: 'App',
  components: {
    AdminTemplate: () => import(/* webpackChunkName: "tpl-admin" */ '/src/views/templates/Admin.vue'),
    ExperimentalTemplate: () => import(/* webpackChunkName: "tpl-experimental" */ '/src/views/templates/Experimental.vue'),
    MockupTemplate: () => import(/* webpackChunkName: "tpl-mockup" */ '/src/views/templates/Mockup.vue'),
    MobileTemplate: () => import(/* webpackChunkName: "tpl-mobile" */ '/src/views/templates/Mobile.vue'),
    PortalTemplate: () => import(/* webpackChunkName: "tpl-portal" */ '/src/views/templates/Portal.vue'),
    PartnerTemplate: () => import(/* webpackChunkName: "tpl-partner" */ '/src/views/templates/Partner.vue'),
    StandardTemplate: () => import(/* webpackChunkName: "tpl-standard" */ '/src/views/templates/Standard.vue'),
  },
  data() {
    return {
      user: false,
      userLoaded: isPrerenderBuild(),
      authBootstrapGeneration: 0,
      unsubscribeAuthBootstrap: null,
    }
  },
  created() {
    if (isPrerenderBuild()) {
      return
    }
    this.unsubscribeAuthBootstrap = subscribeAuthBootstrap(async (payload) => {
      const generation = ++this.authBootstrapGeneration
      this.userLoaded = false

      const authUser = payload?.authUser || null
      if (!authUser) {
        // subscribeAuthBootstrap fires once with a stale logged-out snapshot before
        // authStateReady / onAuthStateChanged finish — wait before treating the
        // session as definitively signed out (portal chrome depends on `user`).
        const boot = await whenAuthBootstrapReady()
        if (generation !== this.authBootstrapGeneration) return

        if (boot?.authUser) {
          try {
            const tokenResult = await boot.authUser.getIdTokenResult()
            this.logIdTokenForDebug(boot.authUser, tokenResult, 'after authBootstrap (recovered)')
            this.user = await this.bootstrapSessionUser(boot.authUser)
            if (generation !== this.authBootstrapGeneration) return
            this.userLoaded = true
            this.redirectAuthenticatedUserIfNeeded()
          } catch (error) {
            console.error('[App] auth bootstrap failed (recovered user):', error)
            this.user = this.buildSessionUserFromAuth(boot.authUser)
            this.userLoaded = true
            this.redirectAuthenticatedUserIfNeeded()
          }
          return
        }

        this.user = false
        this.userLoaded = true
        return
      }

      try {
        const tokenResult = await authUser.getIdTokenResult()
        this.logIdTokenForDebug(authUser, tokenResult, 'after authBootstrap')

        this.user = await this.bootstrapSessionUser(authUser)
        if (generation !== this.authBootstrapGeneration) return

        this.userLoaded = true
        this.redirectAuthenticatedUserIfNeeded()
      } catch (error) {
        console.error('[App] auth bootstrap failed:', error)
        this.user = this.buildSessionUserFromAuth(authUser)
        this.userLoaded = true
      }
    })
  },
  beforeDestroy() {
    if (typeof this.unsubscribeAuthBootstrap === 'function') {
      this.unsubscribeAuthBootstrap()
      this.unsubscribeAuthBootstrap = null
    }
  },
  methods: {
    /**
     * Parsed claims from getIdTokenResult (no global `firebase` in console).
     * Logs when the Firebase client is in development (hostname / init.js), when NODE_ENV !== 'production',
     * or on production host when enabled: ?debugIdToken=1 or sessionStorage.renedian_debug_id_token = '1' then reload.
     */
    shouldLogIdTokenForDebug() {
      if (typeof window === 'undefined') return false
      if (firebaseClientEnvironment === 'development') return true
      if (process.env.NODE_ENV !== 'production') return true
      try {
        if (sessionStorage.getItem('renedian_debug_id_token') === '1') return true
      } catch (e) { /* private mode, etc. */ }
      try {
        return new URLSearchParams(window.location.search).get('debugIdToken') === '1'
      } catch (e) {
        return false
      }
    },
    logIdTokenForDebug(authUser, tokenResult, label = '') {
      if (!this.shouldLogIdTokenForDebug()) return
      const suffix = label ? ` ${label}` : ''
      console.log(`[App] idToken${suffix}`, {
        uid: authUser?.uid,
        email: authUser?.email,
        claims: tokenResult?.claims || {},
        expirationTime: tokenResult?.expirationTime,
      })
    },
    buildSessionUserFromAuth(authUser) {
      return {
        uid: authUser.uid,
        contactId: null,
        profile: {
          name: {
            full: authUser.displayName || '',
            first: '',
            last: '',
          },
          email: authUser.email || '',
          contact: {
            email: authUser.email || '',
            phone: authUser.phoneNumber || '',
          },
          address: {},
          avatar: authUser.photoURL || '',
          verified: !!authUser.emailVerified,
          registered: Date.now(),
        }
      }
    },
    mergeSessionUser(authUser, storedUser, contactProfileSeed, contactId) {
      const baseUser = this.buildSessionUserFromAuth(authUser)
      const storedProfile = storedUser?.profile || {}
      const seededProfile = contactProfileSeed || {}
      const mergedProfile = {
        ...baseUser.profile,
        ...storedProfile,
        ...seededProfile,
        name: {
          ...baseUser.profile.name,
          ...(storedProfile.name || {}),
          ...(seededProfile.name || {}),
        },
        contact: {
          ...baseUser.profile.contact,
          ...(storedProfile.contact || {}),
          ...(seededProfile.contact || {}),
        },
        address: {
          ...baseUser.profile.address,
          ...(storedProfile.address || {}),
          ...(seededProfile.address || {}),
        },
      }

      mergedProfile.email = authUser.email || mergedProfile.email || mergedProfile.contact?.email || ''
      mergedProfile.contact.email = authUser.email || mergedProfile.contact.email || mergedProfile.email || ''
      mergedProfile.contact.phone = mergedProfile.contact.phone || authUser.phoneNumber || ''
      mergedProfile.avatar = mergedProfile.avatar || authUser.photoURL || ''
      mergedProfile.verified = !!authUser.emailVerified

      if (!mergedProfile.name.full) {
        mergedProfile.name.full = `${mergedProfile.name.first || ''} ${mergedProfile.name.last || ''}`.trim()
      }

      const sessionUser = {
        ...baseUser,
        ...(storedUser || {}),
        uid: authUser.uid,
        contactId: contactId || null,
        profile: mergedProfile,
      }

      delete sessionUser.stripe_customer_id
      delete sessionUser.saved_payment_method
      delete sessionUser.payment
      delete sessionUser.wallet

      const hasEmptyName = !sessionUser.profile?.name?.first && !sessionUser.profile?.name?.last
      const hasEmptyPhone = !sessionUser.profile?.contact?.phone
      const hasEmptyAddress = !sessionUser.profile?.address?.street && !sessionUser.profile?.address?.city
      sessionUser.needsProfilePopulation = hasEmptyName || (hasEmptyName && hasEmptyPhone && hasEmptyAddress)

      return sessionUser
    },
    async bootstrapSessionUser(authUser) {
      const db = getFirestore()
      const tokenResult = await authUser.getIdTokenResult()
      const contactId = tokenResult?.claims?.contactId || null

      let contactProfileSeed = null
      let contactSavedPayment = null
      let contactStripeCustomerId = null
      let contactWallet = null
      if (contactId) {
        try {
          const contactSnap = await getDoc(doc(db, 'contacts', contactId))
          if (contactSnap.exists()) {
            const c = contactSnap.data() || {}
            contactProfileSeed = c.profile || {}
            if (c.wallet && typeof c.wallet === 'object') {
              contactWallet = c.wallet
            }
            const pay = c.payment || {}
            if (pay.saved_payment_method != null) {
              contactSavedPayment = pay.saved_payment_method
            }
            if (pay.stripe_customer_id) {
              contactStripeCustomerId = pay.stripe_customer_id
            }
            if (contactSavedPayment == null && c.saved_payment_method != null) {
              contactSavedPayment = c.saved_payment_method
            }
            if (!contactStripeCustomerId && c.stripe_customer_id) {
              contactStripeCustomerId = c.stripe_customer_id
            }
          }
        } catch (error) {
          console.warn('[App] Contact doc hydration failed:', error?.code || error?.message || error)
        }
      }

      const sessionUser = this.mergeSessionUser(authUser, null, contactProfileSeed, contactId)
      if (contactStripeCustomerId) {
        sessionUser.stripe_customer_id = contactStripeCustomerId
      }
      if (contactSavedPayment != null) {
        sessionUser.saved_payment_method = contactSavedPayment
      }
      if (contactWallet) {
        sessionUser.wallet = contactWallet
      }
      return sessionUser
    },
    redirectAuthenticatedUserIfNeeded() {
      if (this.$route?.name === 'PortalLogin') {
        this.go('PortalDashboard')
        return
      }

      if (this.$route?.name === 'PartnerLogin') {
        this.go('PartnerDashboard')
      }
    },
    // Employee record creation is server-managed; no client writes here
    saveUser() {
      const auth = getAuth()
      const db = getFirestore()
      if (!auth.currentUser?.uid || !this.user) return

      const contactId = this.user.contactId
      if (contactId) {
        const contactRef = doc(db, 'contacts', contactId)
        const payload = {
          profile: this.user.profile || {},
          updatedAt: serverTimestamp(),
        }
        if (this.user.stripe_customer_id !== undefined) {
          payload['payment.stripe_customer_id'] = this.user.stripe_customer_id
        }
        if (this.user.saved_payment_method !== undefined) {
          payload['payment.saved_payment_method'] = this.user.saved_payment_method
        }
        setDoc(contactRef, payload, { merge: true }).then(() => {
          console.log('Contact profile saved')
        }).catch(() => {
          console.log('Contact profile could not be saved')
        })
        return
      }

      console.warn('[App] saveUser skipped: no contactId on session (sign in again or open the portal to sync your account).')
    },
    loginRouteForTemplate() {
      const template = this.$route?.meta?.template
      if (template === 'Partner') return 'PartnerLogin'
      if (template === 'Admin') return 'AdminLogin'
      return 'PortalLogin'
    },
    logout() {
      this.userLoaded = false
      // Set before signOut so a session that echoes back from another tab is
      // rejected by the auth listener in authBootstrap.js (see signOutMarker.js).
      markSignedOut()
      const auth = getAuth()
      signOut(auth).then(() => {
        this.user = false
        this.go(this.loginRouteForTemplate())
      }).catch((error) => {
        console.error('[App] sign-out failed:', error)
        this.userLoaded = true
      })
    },
  },
  computed: {
    currentTemplate() {
      return (this.$route.meta.template || 'Standard') + 'Template';
    },
    // The homepage is prerendered with content baked in and needs no auth, so it
    // must never be covered by the auth bootstrap spinner. Other routes (portal,
    // partner, admin, login) keep the overlay until auth resolves.
    showBootstrapOverlay() {
      return !this.userLoaded && this.$route.name !== 'Landing';
    },
  },
}
</script>
<style lang="scss">
  .app-bootstrap-fill {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: #f4f0e3;
  }
  .height-100 {
    height: 100%;
  }
  .height-100-vh {
    height: 100vh !important;
  }
  .flex-none {
    flex: none !important;
    overflow: auto;
  }
  .flex-1 {
    flex: 1 !important;
    /* Allow content like list bullets to render outside child boxes where needed */
    overflow: visible;
  }
  .flex-2 {
    flex: 2 !important;
    overflow: auto;
  }
  .flex-3 {
    flex: 3 !important;
    overflow: auto;
  }
  .v-card__actions {
    background: #f3f3f3 !important;
  }
  /* Renedian Colour Palette */
  .renedian-beige--text {
    color: #f4f0e3 !important;
  }
  .renedian-dark-beige--text {
    color: #e9debd !important;
  }
  .renedian-dark-earth--text {
    color: #c9b295 !important;
  }
  .renedian-dark-brown--text {
    color: #25211a !important;
  }
  .renedian-bright-orange--text {
    color: #f58752 !important;
  }
  .renedian-burnt-orange--text {
    color: #d86226 !important;
  }
  .renedian-red--text {
    color: #bd4426 !important;
  }
  .renedian-grey--text {
    // color: #666666 !important;
    color: #534c45 !important;
  }
  .renedian-brown--text {
    color: #76675d !important;
  }
  .renedian-forest-green--text {
    color: #48622e !important;
  }
  .renedian-beige {
    background-color: #f4f0e3;
  }
  .renedian-dark-beige {
    background-color: #e9debd;
  }
  .renedian-blue-background {
    background: #367b8a !important;
  }
  .renedian-dark-earth--background {
    background-color: #c9b295 !important;
  }
  .renedian-light-dark-earth--background {
    background-color: #ede7dd !important;
  }
  .renedian-white--background {
    background-color: #ffffff !important;
  }
  .renedian-light-dark-earth--text {
    color: #ede7dd !important;
  }
  .renedian-burnt-orange {
    background-color: #d86226;
  }
  .renedian-red {
    background-color: #bd4426;
  }
  .renedian-dark-forest-green--background {
    background-color: #192310 !important;
  }
  .renedian-light-forest-green--text {
    color: #465a33 !important;
  }
  .renedian-dark-forest-green--text {
    color: #192310 !important;
  }

  .hover-pointer:hover {
    cursor: pointer;
    opacity: 0.7
  }

  @font-face {
    font-family: 'Gotham';
    src: url('~@/assets/fonts/gotham-bold.woff') format('woff');
    font-weight: bold;
    font-style: normal;
  }
  @font-face {
    font-family: 'Renedian';
    src: url('~@/assets/fonts/renedian-font.woff2') format('woff2'),
        url('~@/assets/fonts/renedian-font.woff') format('woff');
    font-weight: normal;
    font-style: normal;
  }
  @font-face {
    font-family: 'Lato';
    src: url('~@/assets/fonts/lato-regular.ttf') format('ttf');
    font-weight: normal;
    font-style: normal;
  }
  @font-face {
    font-family: 'Lato-Bold';
    src: url('~@/assets/fonts/lato-bold.ttf') format('ttf');
    font-weight: bold;
    font-style: normal;
  }
  @font-face {
    font-family: 'Lato-Italic';
    src: url('~@/assets/fonts/lato-italic.ttf') format('ttf');
    font-weight: normal;
    font-style: italic;
  }
  .g-font {
    font-family: 'Gotham', sans-serif;
    letter-spacing: 1px;
    font-size: 17px;
    text-transform: uppercase;
  }
  .r-font {
    font-family: 'Renedian', sans-serif;
    letter-spacing: 1px;
    font-size: 17px;
    text-transform: uppercase;
  }
  .font-lato {
    font-family: 'Lato', sans-serif;
  }
  .font-lato-bold {
    font-family: 'Lato-Bold', sans-serif;
    font-weight: bold;
  }
  .font-lato-italic {
    font-family: 'Lato-Italic', sans-serif;
    font-style: italic;
  }
  .r-title {
    font-family: 'Renedian', sans-serif;
    color: #bd4426;
    font-size: 30px;
  }

  /* ============================================
     BRAND TYPOGRAPHY UTILITIES (global)
     --------------------------------------------
     Jost / Sanchez / Barlow are loaded via <link>
     in public/index.html so these classes work on
     every template (Standard, Admin, Portal, Vendor,
     etc.) without each one re-importing fonts.
     Mirrors the legacy classes previously defined
     only inside src/views/templates/Standard.vue.
     ============================================ */
  .jost-light {
    font-family: 'Jost', sans-serif;
    font-optical-sizing: auto;
    font-weight: 300;
    font-style: normal;
  }
  .jost-regular {
    font-family: 'Jost', sans-serif;
    font-optical-sizing: auto;
    font-weight: 400;
    font-style: normal;
  }
  .jost-medium {
    font-family: 'Jost', sans-serif;
    font-optical-sizing: auto;
    font-weight: 500;
    font-style: normal;
  }
  .jost-semibold {
    font-family: 'Jost', sans-serif;
    font-optical-sizing: auto;
    font-weight: 600;
    font-style: normal;
  }
  .jost-bold {
    font-family: 'Jost', sans-serif;
    font-optical-sizing: auto;
    font-weight: 700;
    font-style: normal;
  }
  .jost-extrabold {
    font-family: 'Jost', sans-serif;
    font-optical-sizing: auto;
    font-weight: 800;
    font-style: normal;
  }
  .jost-black {
    font-family: 'Jost', sans-serif;
    font-optical-sizing: auto;
    font-weight: 900;
    font-style: normal;
  }
  .sanchez-regular {
    font-family: 'Sanchez', Georgia, serif;
    font-weight: 400;
    font-style: normal;
  }
  .sanchez-italic,
  .sanchez-regular-italic {
    font-family: 'Sanchez', Georgia, serif;
    font-weight: 400;
    font-style: italic;
  }
  .barlow-light {
    font-family: 'Barlow', sans-serif;
    font-weight: 300;
    font-style: normal;
  }
  .barlow-regular {
    font-family: 'Barlow', sans-serif;
    font-weight: 400;
    font-style: normal;
  }
  .barlow-regular-italic {
    font-family: 'Barlow', sans-serif;
    font-weight: 400;
    font-style: italic;
  }
  .barlow-medium {
    font-family: 'Barlow', sans-serif;
    font-weight: 500;
    font-style: normal;
  }
  .barlow-bold {
    font-family: 'Barlow', sans-serif;
    font-weight: 700;
    font-style: normal;
  }
  .barlow-bold-italic {
    font-family: 'Barlow', sans-serif;
    font-weight: 700;
    font-style: italic;
  }
  .barlow-condensed-regular {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 400;
    font-style: normal;
  }
  .barlow-condensed-regular-italic {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 400;
    font-style: italic;
  }
  .barlow-condensed-bold {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 700;
    font-style: normal;
  }
  .barlow-condensed-bold-italic {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 700;
    font-style: italic;
  }

  .rounded-3 {
    border-radius: 3px !important;
  }
  .rounded-4 {
    border-radius: 4px !important;
  }
  .rounded-0 {
    border-radius: 0 !important;
  }

  /* ============================================
     RESPONSIVE FONT SIZE SYSTEM
     ============================================ */
  
  /* CSS Custom Properties for Typographic Scale */
  :root {
    /* Base font size scale using clamp() for fluid typography
       Optimized for mobile-first: larger minimums for better readability
       Formula: clamp(min-mobile, preferred-scaling, max-desktop)
    */

    /* Visually linear scale: geometric progression from XS to HUGE */
    /* Min ratio: 2.5/0.8 = 3.125 over 5 steps = 1.258 per step */
    /* Max ratio: 4.5/1 = 4.5 over 5 steps = 1.356 per step */
    --font-size-xs: clamp(0.8rem, 0.5vw + 0.85rem, 1rem);        /* 12.8px - 16px */
    --font-size-sm: clamp(1.01rem, 0.75vw + 0.92rem, 1.36rem);   /* 16.16px - 21.76px */
    --font-size-md: clamp(1.27rem, 1vw + 1rem, 1.84rem);         /* 20.32px - 29.44px */
    --font-size-lg: clamp(1.6rem, 1.5vw + 1.1rem, 2.5rem);       /* 25.6px - 40px */
    --font-size-xl: clamp(2.01rem, 2vw + 1.25rem, 3.39rem);      /* 32.16px - 54.24px */
    --font-size-huge: clamp(2.5rem, 3vw + 1.4rem, 4.5rem);       /* 40px - 72px */
  }

  /* Responsive Font Size Classes (clamp-based) */
  .font-size-xs {
    font-size: var(--font-size-xs);
    line-height: 2rem;
  }

  .font-size-sm {
    font-size: var(--font-size-sm) !important;
  }

  .font-size-md {
    font-size: var(--font-size-md);
  }

  .font-size-lg {
    font-size: var(--font-size-lg);
  }

  .font-size-xl {
    font-size: var(--font-size-xl);
  }

  .font-size-huge {
    font-size: var(--font-size-huge);
  }

  /* Fixed Pixel Sizes (for backward compatibility) */


  @for $i from 1 through 20 {
    .line-height-#{$i} {
      line-height: $i * 0.5rem !important;
    }
  }

  @for $i from 6 through 40 {
    .font-size-#{$i} {
      font-size: #{$i}px !important;
    }
  }

  /* Viewport-based Sizes (for backward compatibility) */
  @for $i from 1 through 20 {
    .font-size-#{$i}-vw {
      font-size: $i * 0.5vw !important;
    }
  }
  @for $i from 1 through 20 {
    .width-#{$i * 5} {
      width: $i * 5% !important;
    }
  }
  @for $i from 1 through 20 {
    .width-#{$i * 5}-vw {
      width: $i * 5vw !important;
      max-width: 1904px;
    }
  }

  /* Public page body inset. Matches RippedHeader hero gutters so content
     stays inside the 1904px site column instead of using viewport units. */
  .site-content {
    width: 100%;
    box-sizing: border-box;
    padding-left: min(5vw, 80px) !important;
    padding-right: min(5vw, 80px) !important;
  }

  .position-absolute {
    position: absolute;
  }
  .position-relative {
    position: relative;
  }
  .top-0 {
    top: 0;
  }
  .bottom-0 {
    bottom: 0;
  }
  
  /* Responsive Button Classes */
  .btn-responsive-lg {
    --btn-height: 8vw;
    height: var(--btn-height);
    font-size: calc(var(--btn-height) * 0.25) !important; /* 25% of button height */
  }
  .btn-responsive-md {
    --btn-height: 6vw;
    height: var(--btn-height);
    font-size: calc(var(--btn-height) * 0.25) !important; /* 25% of button height */
  }
  .btn-responsive-sm {
    --btn-height: 5vw;
    height: var(--btn-height);
    font-size: calc(var(--btn-height) * 0.25) !important; /* 25% of button height */
  }
  
  /* Responsive flex utilities following Vuetify's breakpoint system */
  @media (min-width: 960px) {
    .flex-md-1 {
      flex: 1 !important;
    }
    .flex-md-0 {
      flex: 0 !important;
    }
    .flex-md-none {
      flex: none !important;
    }
  }

  .footer-offset {
    padding-bottom: 100px;
  }

  .cursor-pointer {
    cursor: pointer;
    user-select: none;
  }

  .cursor-pointer:hover {
    opacity: 0.8;
  }
  .z-index-0 {
    z-index: 0;
  }
  .z-index-1 {
    z-index: 1;
  }
  .z-index-2 {
    z-index: 2;
  }
  .z-index-3 {
    z-index: 3;
  }
  .z-index-4 {
    z-index: 4;
  }

  /* Lifted photo shadow */
  .lifted-photo {
    rotate: 4deg;
    border: 1vw solid white;
    box-shadow:
      0 12px 20px rgba(0, 0, 0, 0.18),
      0 24px 40px rgba(0, 0, 0, 0.12);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .lifted-photo-right {
    rotate: -4deg;
  }
  .lifted-photo:hover {
    transform: translateY(-2px) rotate(0.2deg);
    box-shadow:
      0 18px 28px rgba(0, 0, 0, 0.22),
      0 32px 60px rgba(0, 0, 0, 0.18);
  }

  /* Insurance description markdown (cards + portal) */
  .insurance-markdown ul,
  .insurance-markdown ol {
    margin: 0.25em 0;
    padding-left: 1.25em;
  }
  .insurance-markdown p {
    margin: 0.25em 0;
  }
  .insurance-markdown p:first-child {
    margin-top: 0;
  }
  .insurance-markdown p:last-child {
    margin-bottom: 0;
  }
</style>